import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { toast } from "react-toastify";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 13,
  },
  rowMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  listTitle: {
    marginTop: theme.spacing(2),
    fontWeight: 600,
  },
}));

const STATUS = {
  pending: { label: "Agendada", color: "primary" },
  sending: { label: "Enviando", color: "primary" },
  sent: { label: "Enviada", color: "default" },
  failed: { label: "Falhou", color: "secondary" },
  canceled: { label: "Cancelada", color: "default" },
};

const pad = (value) => String(value).padStart(2, "0");

// "2026-10-30T15:00" no fuso do navegador, que e o que o <input
// type="datetime-local"> le e escreve.
const toLocalInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;

const defaultSendAt = () => {
  const date = new Date(Date.now() + 60 * 60 * 1000);
  date.setMinutes(Math.ceil(date.getMinutes() / 5) * 5, 0, 0);
  return toLocalInput(date);
};

const formatDateTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const ERRORS = {
  ERR_SCHEDULED_MESSAGE_EMPTY: "Escreva a mensagem.",
  ERR_SCHEDULED_MESSAGE_TOO_LONG: "Mensagem longa demais.",
  ERR_SCHEDULED_MESSAGE_INVALID_DATE: "Data e hora inválidas.",
  ERR_SCHEDULED_MESSAGE_IN_THE_PAST: "Escolha uma data e hora no futuro.",
  ERR_SCHEDULED_MESSAGE_TOO_FAR: "O agendamento vai até um ano à frente.",
  ERR_SCHEDULED_MESSAGE_NOT_PENDING: "Essa mensagem já foi enviada ou cancelada.",
};

const showError = (err) => {
  const code = err?.response?.data?.error;
  if (code && ERRORS[code]) {
    toast.error(ERRORS[code]);
    return;
  }
  toastError(err);
};

// Botao de relogio do campo de mensagem: agenda o texto para sair depois e
// lista o que ja esta agendado neste chamado (com o numero no icone).
const ScheduleMessage = ({ ticketId, draft, signName, disabled, onScheduled }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState("");
  const [sendAt, setSendAt] = useState(defaultSendAt());

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoadingList(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/scheduled-messages`);
      setItems(Array.isArray(data?.scheduled) ? data.scheduled : []);
    } catch (_err) {
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, [ticketId]);

  useEffect(() => {
    setItems([]);
    load();
  }, [load]);

  // O agendador avisa quando envia ou falha: o numero e a lista se atualizam.
  useEffect(() => {
    if (!ticketId) return undefined;
    const socket = openSocket();
    const onEvent = (data) => {
      if (Number(data?.ticketId) === Number(ticketId)) load();
    };
    socket.on("scheduledMessage", onEvent);
    return () => {
      socket.off("scheduledMessage", onEvent);
      socket.disconnect();
    };
  }, [ticketId, load]);

  const pendingCount = items.filter((item) => item.status === "pending").length;

  const handleOpen = () => {
    setText(String(draft || ""));
    setSendAt(defaultSendAt());
    setOpen(true);
    load();
  };

  const handleSchedule = async () => {
    const body = String(text || "").trim();
    if (!body) {
      toast.error(ERRORS.ERR_SCHEDULED_MESSAGE_EMPTY);
      return;
    }
    const when = new Date(sendAt);
    if (Number.isNaN(when.getTime())) {
      toast.error(ERRORS.ERR_SCHEDULED_MESSAGE_INVALID_DATE);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tickets/${ticketId}/scheduled-messages`, {
        body: signName ? `*${signName}:*\n${body}` : body,
        sendAt: when.toISOString(),
      });
      toast.success(`Mensagem agendada para ${formatDateTime(when)}.`);
      setOpen(false);
      if (onScheduled) onScheduled();
      load();
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (item) => {
    try {
      await api.delete(`/scheduled-messages/${item.id}`);
      load();
    } catch (err) {
      showError(err);
    }
  };

  return (
    <>
      <Tooltip title="Agendar mensagem">
        <span>
          <IconButton
            aria-label="scheduleMessage"
            component="span"
            onClick={handleOpen}
            disabled={disabled}
          >
            <Badge badgeContent={pendingCount} color="primary" overlap="circular">
              <ScheduleIcon />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Agendar mensagem</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Enviar em"
            type="datetime-local"
            value={sendAt}
            onChange={(event) => setSendAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Mensagem"
            value={text}
            onChange={(event) => setText(event.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 4000 }}
            helperText={
              signName
                ? `Sai com a sua assinatura (${signName}), como no envio normal.`
                : "Sai sem assinatura, como no envio normal."
            }
          />
          <Typography variant="body2" color="textSecondary">
            Na API oficial do WhatsApp, a mensagem só sai se a janela de 24h do cliente estiver
            aberta na hora do envio. Se o chamado tiver sido encerrado e o cliente abriu outro, ela
            vai para o atendimento atual.
          </Typography>

          <Typography variant="subtitle2" className={classes.listTitle}>
            Agendadas neste chamado {loadingList ? <CircularProgress size={12} /> : null}
          </Typography>
          {!items.length && !loadingList ? (
            <Typography variant="body2" color="textSecondary">
              Nenhuma.
            </Typography>
          ) : null}
          {items.map((item) => {
            const status = STATUS[item.status] || STATUS.pending;
            return (
              <div key={item.id} className={classes.row}>
                <div className={classes.rowText}>
                  <div className={classes.rowMeta}>
                    {formatDateTime(item.sendAt)}
                    {item.userName ? ` · ${item.userName}` : ""}{" "}
                    <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                  </div>
                  {item.body}
                  {item.status === "failed" && item.error ? (
                    <Typography variant="caption" color="error" display="block">
                      {item.error}
                    </Typography>
                  ) : null}
                </div>
                {item.status === "pending" ? (
                  <Button size="small" onClick={() => handleCancel(item)}>
                    Cancelar
                  </Button>
                ) : null}
              </div>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          <Button color="primary" variant="contained" onClick={handleSchedule} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Agendar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduleMessage;
